import { useState } from "react";
import { ShowToast } from "../../../components/toast";
import { useSetCommissionRate } from "../hooks";
import { ClientBilling } from "../types";
import { formatCommissionRate } from "../utils";

/** Click-to-edit commission rate. Owns its own edit state so typing doesn't
 *  re-render the whole table. */
export function RateCell({ client }: { client: ClientBilling }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState("");
  const setRate = useSetCommissionRate();

  const save = () => {
    const newRate = parseFloat(value);
    if (isNaN(newRate) || newRate < 0 || newRate > 100) {
      ShowToast({ type: "error", message: "Rate must be 0–100" });
      return;
    }
    setRate.mutate(
      {
        userId: client.userId,
        commissionRate: newRate / 100,
        billingEmail: client.stripeEmail || client.email || "",
        billingType: client.billingType || undefined,
        displayBasePrice: client.displayBasePrice ?? undefined,
        displayCommissionMarkup: client.displayCommissionMarkup ?? undefined,
        showOnboardingFee: client.showOnboardingFee ?? undefined,
      },
      { onSuccess: () => setEditing(false) },
    );
  };

  if (!editing) {
    return (
      <button
        className="text-sm font-medium text-content-primary hover:text-primary hover:underline decoration-dashed underline-offset-2 cursor-pointer"
        onClick={(e) => {
          e.stopPropagation();
          setValue(formatCommissionRate(client.commissionRate));
          setEditing(true);
        }}
        title="Click to edit rate"
      >
        {formatCommissionRate(client.commissionRate)}%
      </button>
    );
  }

  return (
    <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
      <input
        type="number"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        className="w-16 px-1.5 py-1 text-sm border border-primary rounded focus:outline-none focus:ring-1 focus:ring-primary bg-white"
        min="0"
        max="100"
        step="0.5"
        autoFocus
        onKeyDown={(e) => {
          if (e.key === "Enter") save();
          else if (e.key === "Escape") setEditing(false);
        }}
      />
      <span className="text-xs text-content-tertiary">%</span>
      <button
        onClick={save}
        disabled={setRate.isPending}
        className="px-2 py-0.5 rounded text-xs font-medium bg-primary text-white hover:bg-primary-hover transition-colors"
      >
        {setRate.isPending ? "..." : "Save"}
      </button>
      <button
        onClick={() => setEditing(false)}
        className="px-2 py-0.5 rounded text-xs font-medium border border-border-secondary text-content-secondary hover:bg-surface-secondary transition-colors"
      >
        Cancel
      </button>
    </div>
  );
}
